import {
  CssCustomProperties,
  CssProperties,
} from '@wix/component-protocol/schema';

import { PlatformPrivateServices } from '../../../PlatformPrivateServices';
import type {
  FontFamilyAndWeight,
  SelectFontFamilyOptions,
} from '@wix/editor-platform-sdk-types';

import { type FontWeight } from '@wix/document-services-types';

interface ISelectFontWeightUIParams {
  onChange?: (value: FontWeight) => void;
  target?: HTMLElement;
}

type IConnectedSelectFontWeightParams = ISelectFontWeightUIParams & {
  fontStyleItemKey?: string;
  fontFamilyStyleItemKey?: string;
  fontWeightStyleItemKey?: string;
};

interface IConnectedSelectFontParams {
  fontStyleItemKey?: string;
}

export type ISelectFontParams = IConnectedSelectFontParams &
  SelectFontFamilyOptions;

interface ISelectFontWeightContext {
  onChange: ((value: FontWeight) => void) | undefined;
  styleDefinitions: {
    cssProperties: CssProperties;
    cssCustomProperties: CssCustomProperties;
  };
  styles: Record<string, FontStyleItemValue>;
}

interface ISelectFontContext {
  onChange: ((value: FontFamilyAndWeight) => void) | undefined;
  styleDefinitions: {
    cssProperties: CssProperties;
    cssCustomProperties: CssCustomProperties;
  };
  styles: Record<string, FontStyleItemValue>;
}

// TODO: find the generic type of StyleValue which accepts the inner type type
type FontStyleItemValue = {
  value: FontFamilyAndWeight;
  defaultValue: FontFamilyAndWeight;
};

export type ISelectFontWeightParams =
  | ISelectFontWeightUIParams
  | IConnectedSelectFontWeightParams;

const FONT_PROPERTIES = ['fontFamily', 'font'];
const FONT_WEIGHT_PROPERTIES = ['font-weight'];

export class HostFontWeightSelectorAPI {
  static isFontProperty(property?: string) {
    return property ? FONT_PROPERTIES.includes(property) : false;
  }

  static isFontWeightProperty(property?: string) {
    return property ? FONT_WEIGHT_PROPERTIES.includes(property) : false;
  }

  constructor(private ps: PlatformPrivateServices) {}

  #getFontFamilyAndWeight(
    keys: IConnectedSelectFontWeightParams,
    context: {
      styleDefinitions?: {
        cssProperties: CssProperties;
        cssCustomProperties: CssCustomProperties;
      };
      styles?: Record<string, FontStyleItemValue>;
    },
  ) {
    if (keys.fontFamilyStyleItemKey && keys.fontWeightStyleItemKey) {
      if (
        !HostFontWeightSelectorAPI.isFontProperty(
          keys.fontFamilyStyleItemKey,
        ) ||
        !HostFontWeightSelectorAPI.isFontWeightProperty(
          keys.fontWeightStyleItemKey,
        )
      ) {
        // TODO: throw error: unsupported property type https://wix.atlassian.net/browse/EP-6428
      }

      return {
        family: context.styles?.[keys.fontFamilyStyleItemKey]?.value.family,
        weight: context.styles?.[keys.fontWeightStyleItemKey]?.value.weight,
      } as FontFamilyAndWeight;
    }

    if (keys.fontStyleItemKey) {
      if (!HostFontWeightSelectorAPI.isFontProperty(keys.fontStyleItemKey)) {
        // TODO: throw error: unsupported property type https://wix.atlassian.net/browse/EP-6428
      }

      return context.styles?.[keys.fontStyleItemKey]
        ?.value as FontFamilyAndWeight;
    }

    // TODO: throw error: invalid font family or weight style item key https://wix.atlassian.net/browse/EP-6428
    throw new Error('Invalid font family or weight style item key');
  }

  selectFontWeight(
    params: IConnectedSelectFontWeightParams,
    context: ISelectFontWeightContext,
  ) {
    const font = this.#getFontFamilyAndWeight(params, context);

    return new Promise<FontFamilyAndWeight>(async (resolve) => {
      const selectedFontWeight = await (this.ps.hostAPI.editor.selectFontWeight(
        {
          fontFamily: font,
        },
      ) as Promise<FontWeight>);

      resolve({
        ...font,
        weight: selectedFontWeight,
      });
    });
  }

  selectFont(params: IConnectedSelectFontParams, context: ISelectFontContext) {
    return new Promise<FontFamilyAndWeight | undefined>(async (resolve) => {
      const font = context.styles[params.fontStyleItemKey!]?.value;
      const fontStyleKey = params.fontStyleItemKey!;
      // For cssProperties, the key itself IS the cssPropertyType (e.g., 'font', 'fontFamily')
      // For cssCustomProperties, we need to check the cssPropertyType field
      const showWeights =
        (fontStyleKey in context.styleDefinitions.cssProperties &&
          fontStyleKey === 'font') ||
        context.styleDefinitions.cssCustomProperties[fontStyleKey]
          ?.cssPropertyType === 'font';

      const newFont = await this.ps.hostAPI.editor.selectFontFamily({
        selectedFontFamily: font,
        showWeights,
      });

      resolve(newFont);
    });
  }
}
